import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getDissertation, postComment } from "./dissertation.api";
import { FinalSubmissionCard } from "./final-submission-card";
import { api } from "@/lib/api";
import { DashboardLayout } from "@/app/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toaster } from "@/components/Toaster";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { ChevronDown } from "lucide-react";
import { formatDate, formFileDownloadByPathUrl, formFileDownloadMediaUrl, handleMutationError } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { routes } from "@/app/routes/paths";

export const DissertationDetailsPage: React.FC = () => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const [filesExpanded, setFilesExpanded] = useState(false);
	const [commentsExpanded, setCommentsExpanded] = useState(true);
	const [filesPage, setFilesPage] = useState(1);
	const [commentsPage, setCommentsPage] = useState(1);
	const commentsObserverTarget = useRef<HTMLDivElement>(null);
	const FILES_PER_PAGE = 5;
	const COMMENTS_PER_PAGE = 10;

	const { data: dissertationResp, isLoading: isDissertationLoading } = useQuery({
		queryKey: ["dissertation"],
		queryFn: getDissertation,
	});

	const dissertation = dissertationResp?.data ?? null;

	const { data: filesResp, isLoading: isFilesLoading } = useQuery({
		queryKey: ["dissertation-files"],
		queryFn: async () => {
			const res = await api.get(`/dissertations/files`);
			return res.data;
		},
		enabled: !!dissertation,
	});

	const files: {
		name: string;
		path: string;
	}[] = Object.values(filesResp?.data ?? []);
	const paginatedFiles = files.slice(0, filesPage * FILES_PER_PAGE);
	const hasMoreFiles = paginatedFiles.length < files.length;

	const { data: commentsResp, isLoading: isCommentsLoading } = useQuery({
		queryKey: ["dissertation-comments"],
		queryFn: async () => {
			const res = await api.get(`/dissertations/feedback`);
			return res.data;
		},
		enabled: !!dissertation,
	});

	const comments = commentsResp?.data ?? [];
	const paginatedComments = comments.slice(0, commentsPage * COMMENTS_PER_PAGE);
	const hasMoreComments = paginatedComments.length < comments.length;

	// Infinite scroll trigger for comments
	useEffect(() => {
		const observer = new IntersectionObserver(([entry]) => {
			if (entry.isIntersecting && hasMoreComments && commentsExpanded) {
				setCommentsPage(p => p + 1);
			}
		}, { threshold: 0.1 });

		if (commentsObserverTarget.current) observer.observe(commentsObserverTarget.current);
		return () => observer.disconnect();
	}, [hasMoreComments, commentsExpanded]);

	const commentMutation = useMutation({
		mutationFn: (data: FormData) => postComment(data),
		onSuccess() {
			toaster("success", "Comment posted");
			queryClient.invalidateQueries({ queryKey: ["dissertation-comments"] });
			setCommentsPage(1);
			form.reset({
				message: "",
				attachment: undefined,
			});
		},
		onError: handleMutationError
	});

	const form = useForm<{ message: string; attachment?: File[] }>({
		defaultValues: {
			message: "",
			attachment: undefined,
		},
	});

	useEffect(() => {
		if (dissertation && dissertation.resubmission_enabled) {
			navigate(routes.DISSERTATION_SUBMISSIONS_PATH);
		}
	}, [dissertation]);

	useEffect(() => {
		if (dissertation?.title) document.title = dissertation.title;
	}, [dissertation]);

	const onSubmit = (values: { message: string; attachment?: File[] }) => {
		const fd = new FormData();
		fd.append("comment", values.message || "");
		values.attachment?.forEach((file) => {
			fd.append("file_attached[]", file);
		});

		commentMutation.mutate(fd);
	};

	return (
		<DashboardLayout>
			<div className="space-y-6">
				<div className="flex items-center justify-between">
					<h1 className="text-2xl font-bold">Dissertation Details</h1>
				</div>

				{isDissertationLoading ? (
					<div>Loading dissertation...</div>
				) : dissertation ? (
					<div className="space-y-4">
						<div className="p-4 border rounded">
							<h2 className="text-xl font-semibold">{dissertation.title}</h2>
							<div className="mt-4 flex items-start justify-between gap-4">
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
									<div>
										<div className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Faculty</div>
										<div className="text-sm mt-1">{dissertation.faculty?.name || "—"}</div>
									</div>
									<div>
										<div className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Subspecialty</div>
										<div className="text-sm mt-1">{dissertation.subspecialty?.name || "—"}</div>
									</div>
									<div>
										<div className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Submitted</div>
										<div className="text-sm mt-1">{dissertation.submitted_at ? new Date(dissertation.submitted_at).toLocaleDateString() : "—"}</div>
									</div>
									<div>
										<div className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Last Updated</div>
										<div className="text-sm mt-1">{dissertation.last_updated ? new Date(dissertation.last_updated).toLocaleDateString() : "—"}</div>
									</div>
								</div>
								<div className="p-4 bg-blue-50 border border-blue-200 rounded-lg min-w-max">
									<div className="text-xs font-semibold text-blue-700 uppercase mb-2">Status</div>
									<div className="text-lg font-bold text-blue-900 mb-3">{dissertation.status_name || dissertation.status}</div>
									<div className="text-xs text-blue-800 leading-relaxed">{dissertation.status_description || "—"}</div>
								</div>
							</div>
							<div className="mt-4">
								<div className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-2">Abstract</div>
								<div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: dissertation.abstract || "" }} />
							</div>
						</div>

						<FinalSubmissionCard dissertation={dissertation} />

						<div className="border rounded overflow-hidden">
							<div className="p-4 bg-gray-50 dark:bg-neutral-700 cursor-pointer flex items-center justify-between hover:bg-gray-100 transition-colors" onClick={() => setFilesExpanded(!filesExpanded)}>
								<div className="flex items-center gap-2">
									<ChevronDown size={20} className={`transform transition-transform ${filesExpanded ? "rotate-0" : "-rotate-90"}`} />
									<h3 className="font-semibold">Submitted Files ({files.length})</h3>
								</div>
							</div>
							{filesExpanded && (
								<div className="p-4 space-y-2">
									{isFilesLoading ? (
										<div>Loading files...</div>
									) : paginatedFiles.length ? (
										<>
											<ul className="space-y-2">
												{paginatedFiles.map((f: { name: string; path: string }) => (
													<li key={f.name} className="flex items-center justify-between p-2 border rounded hover:bg-gray-50">
														<div className="truncate flex-1">{f.name}</div>
														<div className="flex gap-2 ml-2">
															<a href={formFileDownloadByPathUrl(`dissertations/${dissertation.key}/${f.name}`, f.path)} className="text-sm text-gray-600 hover:underline">Download</a>
														</div>
													</li>
												))}
											</ul>
											{hasMoreFiles && (
												<div className="mt-3 text-center">
													<Button variant="outline" onClick={() => setFilesPage(p => p + 1)} size="sm">Load More ({files.length - paginatedFiles.length} remaining)</Button>
												</div>
											)}
										</>
									) : (
										<div className="text-sm text-gray-500">No files found.</div>
									)}
								</div>
							)}
						</div>

						<div className="border rounded overflow-hidden">
							<div className="p-4 bg-gray-50 dark:bg-neutral-700 cursor-pointer flex items-center justify-between hover:bg-gray-100 transition-colors" onClick={() => setCommentsExpanded(!commentsExpanded)}>
								<div className="flex items-center gap-2">
									<ChevronDown size={20} className={`transform transition-transform ${commentsExpanded ? "rotate-0" : "-rotate-90"}`} />
									<h3 className="font-semibold">Comments ({comments.length})</h3>
								</div>
							</div>
							{commentsExpanded && (
								<div className="p-4 space-y-4">

									<div className="">
										<h4 className="font-medium mb-3">Add a comment</h4>
										<Form {...form}>
											<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
												<FormField
													control={form.control}
													name="message"
													render={({ field }) => (
														<FormItem>
															<FormControl>
																<RichTextEditor value={field.value} onChange={field.onChange} />
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>

												<FormField
													control={form.control}
													name="attachment"
													render={({ field: { onChange } }) => (
														<FormItem>
															<FormControl>
																<Input
																	type="file"
																	multiple
																	onChange={(e) => onChange(Array.from(e.target.files ?? []))}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>

												<div className="text-right">
													<Button type="submit" loading={commentMutation.isPending}>Post Comment</Button>
												</div>
											</form>
										</Form>
									</div>
									{isCommentsLoading ? (
										<div>Loading comments...</div>
									) : paginatedComments.length ? (
										<div className="mt-4 pt-4 border-t">
											<div className="space-y-3 max-h-96 overflow-y-auto">
												{paginatedComments.map((c: { id: string; user: { name: string }; created_at: string; comment: string; file_url?: string; original_file_name?: string; attachments?: { id: number | string; file_url: string; original_file_name: string }[] }) => (
													<div key={c.id} className="border rounded p-3 bg-gray-50 dark:bg-neutral-800">
														<div className="text-sm font-medium">{c.user?.name ?? 'Examiner'} <span className="text-xs text-muted-foreground">{formatDate(c.created_at, 'Do MMM, YYYY - h:mm a')}</span></div>
														<div className="mt-2 text-sm prose max-w-none" dangerouslySetInnerHTML={{ __html: c.comment }} />
														{c.attachments && c.attachments.length > 0 ? (
															<div className="mt-5">
																<div className="text-xs font-medium">Attachments</div>
																<ul className="mt-1 space-y-1">
																	{c.attachments.map((attachment) => (
																		<li key={attachment.id} className="flex items-center justify-between text-xs">
																			<a href={formFileDownloadMediaUrl(attachment.file_url)} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
																				<div className="truncate">{attachment.original_file_name}</div>
																			</a>
																		</li>
																	))}
																</ul>
															</div>
														) : c.file_url && (
															<div className="mt-5">
																<div className="text-xs font-medium">Attachments</div>
																<ul className="mt-1 space-y-1">
																		<li className="flex items-center justify-between text-xs">
																		<a href={formFileDownloadMediaUrl(c.file_url)} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
																			<div className="truncate">{c.original_file_name}</div>
																			</a>
																		</li>
																</ul>
															</div>
														)}
													</div>
												))}
											</div>
											<div ref={commentsObserverTarget} className="h-4" />
											{hasMoreComments && (
												<div className="mt-3 text-center">
													<Button variant="outline" onClick={() => setCommentsPage(p => p + 1)} size="sm">Load More ({comments.length - paginatedComments.length} remaining)</Button>
												</div>
											)}
										</div>
									) : (
										<div className="text-sm text-gray-500">No comments yet.</div>
									)}
								</div>
							)}
						</div>
					</div>
				) : (
					<div>No dissertation found.</div>
				)}
			</div>
		</DashboardLayout>
	);
};

export default DissertationDetailsPage;
